import BreadCrumb from 'components/shared/BreadCrumb';
import { useMonitoringQuery } from 'hooks/admin/useMonitoringQuery';
import { Form, Table } from 'react-bootstrap';
import { ResponsiveBar } from '@nivo/bar';

export function AdminMonitoring() {
  const {
    학기_목록,
    학기_로딩,
    학과_목록,
    학과_로딩,
    모니터링_통계,
    모니터링_로딩,
    selectedTermId,
    setSelectedTermId,
    selectedDivision,
    setSelectedDivision,
    handleSearch,
  } = useMonitoringQuery();

  const summary = 모니터링_통계?.data?.summary ?? 모니터링_통계?.summary;
  const deptStats = 모니터링_통계?.data?.departmentStats ?? 모니터링_통계?.departmentStats ?? [];

  // 차트 데이터: 학과별 출석률(단색 바) + Pass/NonPass(누적 바) 2행 구성
  const chartData = deptStats.map((d) => ({
    division: d.division.length > 10 ? d.division.slice(0, 10) + '…' : d.division,
    출석률: Math.round(d.attendanceRate * 10) / 10,
    Pass: d.passRate,
    NonPass: d.nonPassRate,
  }));

  return (
    <main id='main' className='main'>
      <BreadCrumb home={'/'} sublink={'/monitoring'} title={'모니터링'} subTitle={'관리자 모니터링'} />

      {/* 필터 */}
      <div className='card p-3 mb-3'>
        <div className='d-flex align-items-center gap-3 flex-wrap'>
          <div className='d-flex align-items-center gap-2'>
            <label className='white-nowrap fw-bold'>학기</label>
            <Form.Select
              style={{ width: 160 }}
              value={selectedTermId || ''}
              onChange={(e) => {
                setSelectedTermId(e.target.value ? Number(e.target.value) : null);
                setSelectedDivision('');
              }}
              disabled={학기_로딩}
            >
              <option value=''>학기 선택</option>
              {(학기_목록?.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className='d-flex align-items-center gap-2'>
            <label className='white-nowrap fw-bold'>학과</label>
            <Form.Select
              style={{ width: 180 }}
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              disabled={!selectedTermId || 학과_로딩}
            >
              <option value=''>전체</option>
              {(학과_목록?.data ?? 학과_목록 ?? []).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
          </div>

          <button
            className='btn btn-primary'
            onClick={handleSearch}
            disabled={!selectedTermId || 모니터링_로딩}
          >
            조회
          </button>
        </div>
      </div>

      {모니터링_로딩 && (
        <div className='text-center py-5'>
          <div className='spinner-border text-primary' />
        </div>
      )}

      {summary && !모니터링_로딩 && (
        <>
          {/* 요약 카드 */}
          <div className='row mb-3'>
            <div className='col-6 col-md-3'>
              <div className='card info-card p-3 text-center'>
                <p className='--Palette-Gray-70 fz-14 mb-1'>총 수강생</p>
                <h4 className='fw-bold'>{summary.totalStudentCount.toLocaleString()}명</h4>
              </div>
            </div>
            <div className='col-6 col-md-3'>
              <div className='card info-card p-3 text-center'>
                <p className='--Palette-Gray-70 fz-14 mb-1'>전체 출석률</p>
                <h4 className='fw-bold text-primary'>{summary.attendanceRate}%</h4>
              </div>
            </div>
            <div className='col-6 col-md-3'>
              <div className='card info-card p-3 text-center'>
                <p className='--Palette-Gray-70 fz-14 mb-1'>Pass 비율</p>
                <h4 className='fw-bold' style={{ color: '#2ecc71' }}>
                  {summary.passRate}%
                  <span className='fz-12 --Palette-Gray-70 ms-1'>({summary.passCount}명)</span>
                </h4>
              </div>
            </div>
            <div className='col-6 col-md-3'>
              <div className='card info-card p-3 text-center'>
                <p className='--Palette-Gray-70 fz-14 mb-1'>Non-Pass 비율</p>
                <h4 className='fw-bold' style={{ color: '#e74c3c' }}>
                  {summary.nonPassRate}%
                  <span className='fz-12 --Palette-Gray-70 ms-1'>({summary.nonPassCount}명)</span>
                </h4>
              </div>
            </div>
          </div>

          {/* 차트 */}
          {chartData.length > 0 && (
            <div className='card p-3 mb-3'>
              <h5 className='fw-bold mb-3'>학과별 출석률 / Pass·Non-Pass 비율</h5>
              <p className='fz-12 --Palette-Gray-70 mb-2'>
                위 바: 출석률(%) / 아래 바: Pass(초록)·Non-Pass(빨강) 비율(%)
              </p>

              {/* 출석률 바 */}
              <div style={{ height: Math.max(200, chartData.length * 44) }}>
                <ResponsiveBar
                  data={chartData}
                  keys={['출석률']}
                  indexBy='division'
                  layout='horizontal'
                  maxValue={100}
                  margin={{ top: 10, right: 60, bottom: 30, left: 140 }}
                  padding={0.3}
                  colors={['#4266FA']}
                  borderRadius={4}
                  enableLabel
                  labelTextColor='#fff'
                  label={(d) => `${d.value}%`}
                  axisBottom={{ tickValues: [0, 25, 50, 75, 100] }}
                  enableGridY={false}
                  enableGridX
                  gridXValues={[0, 25, 50, 75, 100]}
                  tooltip={({ id, value, indexValue }) => (
                    <div className='bg-white border rounded p-2 fz-12'>
                      <strong>{indexValue}</strong> 출석률: {value}%
                    </div>
                  )}
                />
              </div>

              {/* Pass/NonPass 누적 바 */}
              <div style={{ height: Math.max(200, chartData.length * 44), marginTop: 8 }}>
                <ResponsiveBar
                  data={chartData}
                  keys={['Pass', 'NonPass']}
                  indexBy='division'
                  layout='horizontal'
                  maxValue={100}
                  margin={{ top: 10, right: 60, bottom: 30, left: 140 }}
                  padding={0.3}
                  colors={({ id }) => (id === 'Pass' ? '#2ecc71' : '#e74c3c')}
                  borderRadius={4}
                  enableLabel
                  labelTextColor='#fff'
                  label={(d) => (d.value > 5 ? `${d.value}%` : '')}
                  axisBottom={{ tickValues: [0, 25, 50, 75, 100] }}
                  enableGridY={false}
                  enableGridX
                  gridXValues={[0, 25, 50, 75, 100]}
                  tooltip={({ id, value, indexValue }) => (
                    <div className='bg-white border rounded p-2 fz-12'>
                      <strong>{indexValue}</strong> {id}: {value}%
                    </div>
                  )}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'bottom-right',
                      direction: 'column',
                      translateX: 55,
                      itemWidth: 50,
                      itemHeight: 20,
                      symbolSize: 12,
                    },
                  ]}
                />
              </div>
            </div>
          )}

          {/* 학과별 상세 테이블 */}
          <div className='card p-3'>
            <h5 className='fw-bold mb-3'>학과별 상세 현황</h5>
            <Table bordered hover responsive size='sm'>
              <thead className='table-light text-center'>
                <tr>
                  <th>학과</th>
                  <th>인원</th>
                  <th>출석률</th>
                  <th>Pass</th>
                  <th>Non-Pass</th>
                  <th>Pass율</th>
                  <th>Non-Pass율</th>
                </tr>
              </thead>
              <tbody>
                {deptStats.map((d) => (
                  <tr key={d.division} className='text-center'>
                    <td className='text-start'>{d.division}</td>
                    <td>{d.studentCount}</td>
                    <td>{d.attendanceRate}%</td>
                    <td style={{ color: '#2ecc71', fontWeight: 'bold' }}>{d.passCount}</td>
                    <td style={{ color: '#e74c3c', fontWeight: 'bold' }}>{d.nonPassCount}</td>
                    <td>{d.passRate}%</td>
                    <td>{d.nonPassRate}%</td>
                  </tr>
                ))}
                {deptStats.length > 0 && (
                  <tr className='text-center table-secondary fw-bold'>
                    <td>합계</td>
                    <td>{summary.totalStudentCount}</td>
                    <td>{summary.attendanceRate}%</td>
                    <td style={{ color: '#2ecc71' }}>{summary.passCount}</td>
                    <td style={{ color: '#e74c3c' }}>{summary.nonPassCount}</td>
                    <td>{summary.passRate}%</td>
                    <td>{summary.nonPassRate}%</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {!summary && !모니터링_로딩 && (
        <div className='card p-5 text-center --Palette-Gray-70'>학기를 선택하고 조회 버튼을 클릭하세요.</div>
      )}
    </main>
  );
}
